import React, { ReactElement, useEffect, useState } from 'react';
import { SetField } from './SetField';
import { NamedEntity } from '../../../types/entities/NamedEntity';
import { FieldComponent } from '../../../types/FieldComponent';
import { Status } from '../../../types/entities/Status';
import { compareText } from '../comparators/compareText';
import { SchemaWithClient } from '../../../types/SchemaWithClient';
import {
  Autocomplete, Button,
  darken,
  lighten,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { SlidingPaneAddNew } from '../../Page/SlidingPaneAddNew';
import { styled } from '@mui/material/styles';
import LargePopper from '../assets/LargePopper';
import AddIcon from "@mui/icons-material/Add";

type Props<T extends NamedEntity> = {
  onChange: (value: T) => void;
  schema: any;
  defaultValue: any;
  forcedValue: any;
  disabled: boolean;
  isMissing: boolean;
  accordingToValue: any;
  title?: string;
  mainSchema?: any;
  allValue?: any;
  column?: any;
  columnName?: any;
};

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

const GroupItems = styled('ul')({
  padding: 0,
});

export const SPSelectVenueAddressField = <T extends NamedEntity>({
  onChange,
  defaultValue,
  forcedValue,
  disabled,
  isMissing,
  schema,
  accordingToValue,
  title,
  columnName,
  column,
  allValue,
  mainSchema,
}: Props<T>): ReactElement | null => {
  const [addingEntity, setAddingEntity] = useState(false);
  const [selectOptions, setSelectOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const [currentState, setCurrentState] = useState<any>({
    value: null,
    send: false,
  });

  useEffect(() => {
    if (
      typeof defaultValue !== undefined &&
      defaultValue !== null &&
      defaultValue !== undefined
    ) {
      setSelectOptions([defaultValue]);
    }
  }, []);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setCurrentState({
        value: defaultValue,
        send: false,
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (currentState.send) {
      onChange(currentState.value);
    }
  }, [currentState]);

  const fetchSelectOptions = async (searchText: string) => {
    setLoading(true);

    let string: any = {
      searchValue: searchText,
    };

    if (allValue?.group_id) {
      string.group_id = allValue?.group_id;
      if (columnName === 'address_from'|| column?.columnName === 'meeting_place') {
        string.is_meeting_place = true;
      }
      if (columnName === 'address_to'|| column?.columnName === 'venue') {
        string.is_venue = true;
      }
    }

    const searchParams = new URLSearchParams(string);

    schema.client.readNames(searchParams).then((result: any) => {
      result.unshift({ id: 0, name: '', isButton: true });
      setSelectOptions(result);
      setLoading(false);
    });
  };

  const handleOnChange = (event: any, value: any) => {
    if (value?.isButton) {
      setAddingEntity(true);
    } else {
      setCurrentState({
        value: value,
        send: true,
      });
    }
  };

  return (
    <>
      <Autocomplete
        freeSolo
        fullWidth={true}
        PopperComponent={LargePopper}
        size="small"
        loading={loading}
        options={selectOptions}
        groupBy={(option: any) => {
          let groupName: any = option?.group;

          if (option?.isButton) {
            groupName = null;
          }

          return groupName;
        }}
        getOptionKey={(option: any) => option?.id}
        renderGroup={(params: any) => {
          return (
            <li key={params.key}>
              {params.group && <GroupHeader>{params.group}</GroupHeader>}
              <GroupItems>{params.children}</GroupItems>
            </li>
          );
        }}
        renderOption={(props: any, option: any) => {
          return (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              {option?.isButton && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size={'small'}
                  fullWidth
                >
                  Add new
                </Button>
              )}
              <Stack direction={'row'} gap={1}>
                <Typography>{option.name}</Typography>

                {option?.sub_title && (
                  <>
                    <Typography>({option?.sub_title})</Typography>
                  </>
                )}
                {option?.address_line1 && (
                  <>
                    <Typography>({option?.address_line1})</Typography>
                  </>
                )}
              </Stack>
            </Box>
          );
        }}
        getOptionLabel={(option) => {
          let text = option?.name;

          if (!text) {
            if (option?.accommodation) {
              text = option?.accommodation?.name;
            }else{
              text = option?.address_line1;
            }
          }
          return text;
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option?.id) return false;
          return option.id === value.id;
        }}
        value={currentState.value}
        onOpen={() => {
          fetchSelectOptions('');
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              size="small"
              label={title}
              InputLabelProps={{
                style: {
                  fontSize: 13,
                  paddingTop: 2,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onChange={(event, newValue) => {
          handleOnChange(event, newValue);
        }}
        disabled={disabled}
      />
      {addingEntity && (
        <SlidingPaneAddNew
          schema={schema}
          mainSchema={schema}
          id={'new'}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            if (value) {
              setCurrentState({
                value: value,
                send: true,
              });
              setAddingEntity(false);
            }
          }}
          propertiesFromTable={{}}
          row={{}}
          onClose={(value) => {
            setAddingEntity(false);
          }}
        />
      )}
    </>
  );
};

SPSelectVenueAddressField.tableAdapter = <T extends NamedEntity = NamedEntity>(
  schema: SchemaWithClient<T>
) => {
  const Field: FieldComponent<T, T[]> = ({
    onChange,
    isActive: active,
    defaultValue,
    forcedValue,
    disabled,
    isMissing,
    accordingToValue,
    title,
    allValue,
    columnName,
    column,
    mainSchema,
  }) => {
    return (
      <SPSelectVenueAddressField
        onChange={onChange}
        defaultValue={defaultValue}
        forcedValue={forcedValue}
        disabled={!!disabled}
        isMissing={!!isMissing}
        schema={schema}
        accordingToValue={accordingToValue}
        title={title}
        allValue={allValue}
        mainSchema={mainSchema}
        column={column}
        columnName={columnName}
      />
    );
  };

  Field.includes = (query = [], value) =>
    value
      ? query.length === 0 || query.some((item) => value.id === item.id)
      : false;
  Field.comparator = (one, other) => compareText(one?.name, other?.name);
  Field.FilterRenderer = SetField.tableAdapter(schema).FilterRenderer;

  return Field;
};

function isStatusValue(value: NamedEntity): value is Status {
  if (typeof value === 'string' || value === null) {
    return false;
  }
  return 'color' in value;
}

function isStatusLamp(value: NamedEntity): value is Status {
  return 'status_lamp' in value;
}
